import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useMessage } from "../blocks/message/MessageContext";
import { AppDispatch, RootState } from "../store";
import { setUser } from "../auth/user-slice";
import Ows from "../utils/OwsHandler";
import AccountManager from "../auth/AccountManager";
import LoginForm from "../features/login/LoginForm";
import TOTPForm from "../features/login/TOTPForm";
import EntityHandlerPage from "./EntityHandlerPage";

const LoginPage = () => {
  const dispatch: AppDispatch = useDispatch();
  const user = useSelector((state:  RootState) => state.user);
  const location = useLocation();
  const preventAutoLogin = location.state?.preventAutoLogin;

  const { showMessage } = useMessage();
  const [showTOTPForm, setShowTOTPForm] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!preventAutoLogin) {
      (async () => {
        await silentLogin();
      })();
    } else {
      setInitialized(true);
    }
  }, []);

  const silentLogin = async () => {
    try {
      const currentUser = await Ows.getCurrentUser();
      const blocked = await Ows.checkTotpBlock();

      if (blocked) {
        setShowTOTPForm(true);
        return;
      }

      await Ows.configureSSO();
      dispatch(setUser(currentUser));
    } catch (e) {
      setShowTOTPForm(false);
    } finally {
      setInitialized(true);
    }
  };

  const login = async () => {
    try {
      const isTOTPActive = await Ows.checkTotpBlock();

      if (isTOTPActive) {
        setShowTOTPForm(true);
        return;
      } else {
        await silentLogin();
      }
    } catch (error) {
      setShowTOTPForm(false);
      showMessage("error", "Failed to check TOTP code.");
    }
  };

  const handleAzureLogin = async () => {
    const token = await AccountManager.getUserToken();
    try {
      await Ows.validateViaMsUser(token);
      await silentLogin();
    } catch (error) {
      showMessage("error", (
        <>
          <strong>Token not found.</strong><br/>
          'Login via Azure' can be enabled upon request by contacting 
          <a href="https://www.otys.com/en/support" target="_blank" rel="noopener noreferrer"> OTYS Support </a>
          or through your designated consultant.<br/><br/>
          Please use your standard login via Otys username and password instead.
        </>
      ));
    }
  };

  const handleLogin = async (credentials: { username; password }) => {
    try {
      const isTOTPActive = await Ows.validateUserAndCheckTotp(credentials.username, credentials.password);

      if (isTOTPActive) {
        setShowTOTPForm(true);
      } else {
        await login();
      }
    } catch (error) {
      if (error.message === "Login failed.") {
        showMessage("error", "Invalid credentials.");
      }
    }
  };

  const handleTOTPValidation = async (code) => {
    try {
      await Ows.validateViaTotp(code);
      await login();
    } catch (error) {
      showMessage("error", "Invalid TOTP code.");
    }
  };

  if (!initialized) {
    return null;
  }

  return (
    <>
      {user ? (
        <EntityHandlerPage />
      ) : showTOTPForm ? (
        <TOTPForm onValidate={handleTOTPValidation} />
      ) : (
        <LoginForm onLogin={handleLogin} onAzureLogin={handleAzureLogin} />
      )}
    </>
  );
};

export default LoginPage;
